import { flatten } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { OptionalObjectSchema } from 'yup/lib/object';
import {
  selectPrepareQuestionnaire,
  TaxRequestType,
} from '../../create/PrepareQuestionnaire/slice';
import {
  selectQuestionnaireRoutes,
  setTabValidationState,
} from '../routes/slice';
import { useValidateAutomobiles } from './AdditionalQuestions/Automobiles';
import { useValidateBalanceSheet } from './AdditionalQuestions/BalanceSheet';
import { useValidateBookKeeping } from './AdditionalQuestions/BookKeeping';
import { useValidatCompensation } from './AdditionalQuestions/Compensation';
import { useValidateHomeOffice } from './AdditionalQuestions/HomeOffice';
import { useValidateMiscellaneousInformation } from './AdditionalQuestions/MiscellaneousInformation';
import { useValidatePaycheckProtectionProgramppp } from './AdditionalQuestions/PaycheckProtectionProgramPPP';
import { useValidateProfitAndLossStatement } from './AdditionalQuestions/ProfitandLossStatement';
import { useValidateRentalActivity } from './AdditionalQuestions/RentalActivity';
import { useValidateSpecialDeductionsInformation } from './AdditionalQuestions/SpecialDeductionsInformation';
import { useValidateBusinessType } from './BusinessInfo/BusinessType';
import { useValidateGeneralInfo } from './BusinessInfo/GeneralInfo';
import { useValidateIndustryAndPrimaryActivity } from './BusinessInfo/Industryandprimaryactivity';
import { useValidateBusinessOwners } from './BusinessInfo/Owners';
import { useValidateNonUsCorpOfPartnership } from './Income/CorporationTrust/NonUSCorpofPartnership';
import { useValidateNonUsTrust } from './Income/CorporationTrust/NonUSTrust';
import { useValidateUsCorpOrPartnership } from './Income/CorporationTrust/USCorpOrPartnership';
import { useValidateUsTrust } from './Income/CorporationTrust/USTrust';
import { useValidateHomeSale } from './Income/HomeSale';
import { useValidateInvestments } from './Income/Investments';
import { useValidateOtheIncome } from './Income/OtherIncome';
import { useValidateDividends } from './Income/PassiveIncome/Dividends';
import { useValidateInterest } from './Income/PassiveIncome/Interest';
import { useValidatePension } from './Income/PassiveIncome/Pension';
import { useValidateRentalIncome } from './Income/PassiveIncome/RentalIncome';
import { useValidateSelfEmployment } from './Income/SelfEmployment';
import { useValidateIncomFromTheUs } from './Income/Wages/IncomeFromTheUS';
import { useValidateIncomeOutsideOfTheUs } from './Income/Wages/IncomeOutsideOfTheUS';
import { useValidateAccountsList } from './NonUSFinancialAccounts/AccountsList';
import { useValidateNonUsFinancialAccounts } from './NonUSFinancialAccounts/NonUSFinancialAccounts';
import { useValidateNotes } from './Notes';
import { useValidateCitizenship } from './PersonalDetails/TheBasics/Citizenship';
import { useValidateCovidRelief } from './PersonalDetails/TheBasics/CovidRelief';
import { useValidateDependents } from './PersonalDetails/TheBasics/Dependents';
import { useValidateFamilyForm } from './PersonalDetails/TheBasics/Family';
import { useValidateLogisticsForm } from './PersonalDetails/TheBasics/Logistics';
import { useValidateNameForm } from './PersonalDetails/TheBasics/Name';
import { useValidatePastFillings } from './PersonalDetails/TheBasics/PastFillings';
import { useValidateCurrentAddress } from './PersonalDetails/WhereILive/CurrentAddress';
import { useValidateHistory } from './PersonalDetails/WhereILive/History';
import { useValidateHousingArrangements } from './PersonalDetails/WhereILive/HousingArrangements';
import { useValidateSaleofOtherAssets } from './SoleProprietorship/SaleofOtherAssets';
import { useValidateSoleProprietorship } from './SoleProprietorship/SoleProprietorshipForm1099MISCand1099NEC';
import { useValidateSpouseIncomeDividends } from './SpouseIncome/Dividends';
import { useValidateSpouseIncomeInterest } from './SpouseIncome/Interest';
import { useValidateSpouseIncomePension } from './SpouseIncome/Pension';
import { useValidateSpouseIncomeWages } from './SpouseIncome/Wages';
import { useValidateSummaryForm } from './Summary/ConfirmSummary';
import { useValidateDeductions } from './TaxesDeductions/Deductions';
import { useValidateTaxesPaid } from './TaxesDeductions/TaxesPaid';
import { useValidateTaxesPaidSpouse } from './TaxesDeductions/TaxesPaidSpouse';

function requiredDependentFiles(
  field: IBaseField<any>,
  fieldsRequired: { [x: string]: OptionalObjectSchema<any> }[] = [],
) {
  const dependencies = field.dependencies || [];

  dependencies.forEach((dep) => {
    if (dep.type === 'upload') {
      fieldsRequired.push({
        [dep.name as string]: requiredFileIf(
          field.name as string,
          dep.predicate,
        ),
      });
    } else if (dep.type === 'radio') {
      requiredDependentFiles(dep, fieldsRequired);
    }
  });

  return fieldsRequired;
}

export function requiredFiles(fields: IBaseField<any>[]) {
  return flatten(fields.map((f) => requiredDependentFiles(f, []))).reduce(
    (all, cur) => ({ ...all, ...cur }),
    {},
  );
}

export function requiredFileIf(dependentOn: any, is: any) {
  return Yup.object().when(dependentOn, {
    is,
    then: Yup.object().nullable().required('required').shape({
      url: Yup.string().nullable(),
      title: Yup.string().nullable(),
    }),
    otherwise: Yup.object().nullable(),
  });
}

export function useValidateQuestionnaireForm(
  dotPath: string,
  values: any,
  validationSchema: Yup.AnyObjectSchema,
) {
  const dispatch = useDispatch();
  const { routes } = useSelector(selectQuestionnaireRoutes);

  return () => {
    if (!_.get(routes, dotPath)) return true;
    const isValid = validationSchema.isValidSync(values);

    dispatch(
      setTabValidationState({
        dotPath,
        isValid,
      }),
    );
    return !!isValid;
  };
}

export function useValidateQuestionnaire() {
  const taxType = useSelector(selectPrepareQuestionnaire);

  const individualValidators = [
    // personal_details - basics
    useValidateNameForm(),
    useValidateCitizenship(),
    useValidatePastFillings(),
    useValidateFamilyForm(),
    useValidateDependents(),
    useValidateCovidRelief(),
    useValidateLogisticsForm(),
    // personal_details - where i live
    useValidateCurrentAddress(),
    useValidateHistory(),
    useValidateHousingArrangements(),
    // income - wages
    useValidateIncomFromTheUs(),
    useValidateIncomeOutsideOfTheUs(),
    //  income - corporationTrust
    useValidateNonUsCorpOfPartnership(),
    useValidateUsCorpOrPartnership(),
    useValidateUsTrust(),
    useValidateNonUsTrust(),
    // income - homesale
    useValidateHomeSale(),
    // income - investments
    useValidateInvestments(),
    // icome -  otherIncome
    useValidateOtheIncome(),
    // income - passive income
    useValidateDividends(),
    useValidateInterest(),
    useValidatePension(),
    useValidateRentalIncome(),
    // income -  selfEmployment
    useValidateSelfEmployment(),
    // spouseIncome
    useValidateSpouseIncomeDividends(),
    useValidateSpouseIncomeInterest(),
    useValidateSpouseIncomePension(),
    useValidateSpouseIncomeWages(),
    // taxesDeductions
    useValidateDeductions(),
    useValidateTaxesPaid(),
    useValidateTaxesPaidSpouse(),
    // nonUsFinancialAccounts
    useValidateNonUsFinancialAccounts(),
    useValidateAccountsList(),
    // notes
    useValidateNotes(),
    // summary
    useValidateSummaryForm(),
  ];

  const businessValidators = [
    // business info
    useValidateBusinessType(),
    useValidateGeneralInfo(),
    useValidateBusinessOwners(),
    useValidateIndustryAndPrimaryActivity(),
    // soleProprietorship
    useValidateSoleProprietorship(),
    useValidateSaleofOtherAssets(),
    // additionnal questions
    useValidateAutomobiles(),
    useValidateHomeOffice(),
    useValidateProfitAndLossStatement(),
    useValidateRentalActivity(),
    useValidateBookKeeping(),
    useValidateBalanceSheet(),
    useValidatCompensation(),
    useValidateSpecialDeductionsInformation(),
    useValidatePaycheckProtectionProgramppp(),
    useValidateMiscellaneousInformation(),
    // summary
    useValidateSummaryForm(),
  ];

  return () => {
    return (
      taxType.taxRequestType === TaxRequestType.Individual
        ? individualValidators
        : businessValidators
    )
      .map((fn) => fn())
      .every((isValid) => isValid);
  };
}
